/**
 * Getting it there — the move envelope, the quotes, and the access answer.
 *
 * ── Grey is not green, and that is the whole design ──────────────────────────
 *
 * `shared/access.ts` emits a `caution` for every land column a rule needed and
 * found NULL — never a pass. The roll-up is red for any blocker, amber for any
 * warning, and **grey NOT FULLY VERIFIED for any caution**, which means green
 * is unreachable while a single measurement is missing. The seeded land
 * profile is all NULLs, so a new install is grey and goes green only when
 * somebody has stood on the land with a tape measure.
 *
 * That is not pedantry. An unmeasured gate producing a green light is how
 * somebody drives a trailer to a gate it does not fit through.
 *
 * ── The findings say the numbers ─────────────────────────────────────────────
 *
 * Doing this in arithmetic rather than with a model means every finding is
 * explainable out loud: "the gate is 10.0 ft, the rig is 8.0 ft, and turning
 * needs 33 ft against 45 ft available". English gets that sentence as
 * `shared/access.ts` wrote it; Spanish gets the same numbers with a translated
 * subject, which §10.2 says the client may do from the code.
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import { accessLabel, accessTone, dateText, feet, km, money, pounds, TONES } from '../../lib/format'
import {
  accessFindingText,
  measurementLabel,
  planStatusLabel,
  quoteLegLabel,
  quoteStatusLabel,
} from '../../lib/domain'
import { Chip, Empty, Line, Panel, S } from './ui'
import type { AccessAnswer, QuoteRow, TransportPayload } from './types'

const EN = {
  title: 'Getting it there',
  unavailable: 'Transport could not be loaded.',
  noPlan: 'No move has been planned yet.',
  envelope: 'The rig as it travels',
  length: 'Length',
  width: 'Width',
  height: 'Height',
  weight: 'Weight',
  dimsSource: 'Measurements are',
  origin: 'Coming from',
  roadOrigin: 'Road, mainland',
  roadIsland: 'Road, island',
  ferry: 'Needs the barge',
  crane: 'Needs a crane',
  status: 'Status',
  scheduled: 'Scheduled for',
  pickedUp: 'Picked up',
  delivered: 'Delivered',
  placed: 'Placed',
  quotes: 'Quotes',
  noQuotes: 'Nobody has quoted this yet.',
  access: 'Access',
  noAccess: 'No access check has run.',
  measureThese: 'Measure these and this turns into a real answer',
  blockers: 'Will not fit or cannot get there',
  warnings: 'Fits, with less margin than anyone wants',
  cautions: 'We do not know, because something is not measured',
  includes: 'Includes',
  yes: 'Yes',
  no: 'No',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Cómo llevarlo',
  unavailable: 'No se pudo cargar el transporte.',
  noPlan: 'Todavía no se ha planificado la mudanza.',
  envelope: 'La estructura como viaja',
  length: 'Largo',
  width: 'Ancho',
  height: 'Alto',
  weight: 'Peso',
  dimsSource: 'Las medidas son',
  origin: 'Viene de',
  roadOrigin: 'Carretera, isla grande',
  roadIsland: 'Carretera, isla',
  ferry: 'Necesita la barcaza',
  crane: 'Necesita grúa',
  status: 'Estado',
  scheduled: 'Agendado para',
  pickedUp: 'Recogido',
  delivered: 'Entregado',
  placed: 'Colocado',
  quotes: 'Cotizaciones',
  noQuotes: 'Nadie ha cotizado esto todavía.',
  access: 'Acceso',
  noAccess: 'No ha corrido una verificación de acceso.',
  measureThese: 'Mide estas y esto se convierte en una respuesta de verdad',
  blockers: 'No cabe o no puede llegar',
  warnings: 'Cabe, pero con menos margen del que uno quisiera',
  cautions: 'No sabemos, porque algo no está medido',
  includes: 'Incluye',
  yes: 'Sí',
  no: 'No',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/** Where the numbers came from. `measured` is the only one that is silent. */
const DIMS_SOURCE: Record<Locale, Record<string, string>> = {
  en: { listing: 'From the listing', measured: 'Measured', estimated: 'Estimated' },
  'es-PR': { listing: 'Del anuncio', measured: 'Medidas', estimated: 'Estimadas' },
}

/**
 * The land measurements a rule needed and could not read.
 *
 * `rollUp().missing` mixes land keys and envelope keys — both are cautions.
 * The banner on the detail screen is about the LAND profile specifically, so
 * this filters on `missingLand`, which is the field `shared/access.ts` sets
 * for exactly that case.
 */
export function missingLandMeasurements(access: AccessAnswer | null): string[] {
  if (!access) return []
  const keys = access.findings
    .filter((finding) => finding.severity === 'caution' && typeof finding.missingLand === 'string')
    .map((finding) => String(finding.missingLand))
  return [...new Set(keys)]
}

export default function TransportPanel({
  transport,
  failed = false,
}: {
  transport: TransportPayload | null
  failed?: boolean
}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const plan = transport?.plan ?? null
  const access = transport?.access ?? null
  const quotes = transport?.quotes ?? []
  const level = access?.rollUp.level ?? 'grey'

  return (
    <Panel
      id="transport"
      title={t('title')}
      defaultOpen={level === 'red'}
      badge={<Chip tone={accessTone(level)}>{accessLabel(level, locale)}</Chip>}
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : (
        <>
          {/* ── The envelope ─────────────────────────────────────────────── */}
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: '4px 0 2px' }}>{t('envelope')}</h3>
          {access ? (
            <>
              <Line label={t('length')} first>
                {feet(access.envelope.lengthFt ?? null, locale)}
              </Line>
              <Line label={t('width')}>{feet(access.envelope.widthFt ?? null, locale)}</Line>
              <Line label={t('height')}>{feet(access.envelope.heightFt ?? null, locale)}</Line>
              <Line label={t('weight')}>{pounds(access.envelope.weightLb ?? null, locale)}</Line>
              {access.envelope.dimsSource && (
                <Line label={t('dimsSource')}>
                  {/* `measured` is the only source that is silent; the other two
                      earn a chip, because an estimate quoted as a measurement is
                      how a rig arrives at a gate it does not fit. */}
                  <Chip
                    tone={access.envelope.dimsSource === 'measured' ? TONES.strong : TONES.warn}
                    outlined={access.envelope.dimsSource !== 'measured'}
                  >
                    {DIMS_SOURCE[locale][access.envelope.dimsSource] ?? String(access.envelope.dimsSource)}
                  </Chip>
                </Line>
              )}
            </>
          ) : (
            <Empty>{t('noAccess')}</Empty>
          )}

          {/* ── Access ───────────────────────────────────────────────────── */}
          {access && (
            <div style={{ marginTop: 14 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>{t('access')}</h3>

              <Findings title={t('blockers')} tone={TONES.danger} findings={access.rollUp.blockers} locale={locale} />
              <Findings title={t('warnings')} tone={TONES.warn} findings={access.rollUp.warnings} locale={locale} />
              <Findings title={t('cautions')} tone={TONES.quiet} findings={access.rollUp.cautions} locale={locale} />

              {access.rollUp.missing.length > 0 && (
                <>
                  <p style={{ ...S.note, marginTop: 10, fontWeight: 600 }}>{t('measureThese')}</p>
                  <div style={{ ...S.chipRow, marginTop: 6 }}>
                    {access.rollUp.missing.map((key) => (
                      <Chip key={key} tone={TONES.quiet} outlined>
                        {measurementLabel(key, locale)}
                      </Chip>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── The plan ─────────────────────────────────────────────────── */}
          <div style={{ marginTop: 14 }}>
            {plan ? (
              <>
                <Line label={t('status')} first>
                  <Chip tone={TONES.quiet}>{planStatusLabel(plan.status, locale)}</Chip>
                </Line>
                {plan.origin_label && <Line label={t('origin')}>{plan.origin_label}</Line>}
                {plan.road_km_origin !== null && <Line label={t('roadOrigin')}>{km(plan.road_km_origin, locale)}</Line>}
                {plan.road_km_island !== null && <Line label={t('roadIsland')}>{km(plan.road_km_island, locale)}</Line>}
                <Line label={t('ferry')}>{plan.ferry_required === 1 ? t('yes') : t('no')}</Line>
                <Line label={t('crane')}>{plan.crane_required === 1 ? t('yes') : t('no')}</Line>
                {plan.scheduled_for && <Line label={t('scheduled')}>{dateText(plan.scheduled_for, locale)}</Line>}
                {plan.picked_up_on && <Line label={t('pickedUp')}>{dateText(plan.picked_up_on, locale)}</Line>}
                {plan.delivered_on && <Line label={t('delivered')}>{dateText(plan.delivered_on, locale)}</Line>}
                {plan.placed_on && <Line label={t('placed')}>{dateText(plan.placed_on, locale)}</Line>}
                {plan.notes && <p style={S.note}>{plan.notes}</p>}
              </>
            ) : (
              <Empty>{t('noPlan')}</Empty>
            )}
          </div>

          {/* ── Quotes ───────────────────────────────────────────────────── */}
          <div style={{ marginTop: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px' }}>{t('quotes')}</h3>
            {quotes.length === 0 ? (
              <Empty>{t('noQuotes')}</Empty>
            ) : (
              quotes.map((quote, index) => <Quote key={quote.id} quote={quote} first={index === 0} locale={locale} t={t} />)
            )}
          </div>
        </>
      )}
    </Panel>
  )
}

function Findings({
  title,
  tone,
  findings,
  locale,
}: {
  title: string
  tone: { fg: string; bg: string; line: string }
  findings: AccessAnswer['findings']
  locale: Locale
}) {
  if (findings.length === 0) return null
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: tone.fg }}>{title}</div>
      {findings.map((finding, index) => (
        <p key={`${finding.code}-${index}`} style={{ ...S.sentence, marginTop: 4 }}>
          {accessFindingText(finding, locale)}
        </p>
      ))}
    </div>
  )
}

function Quote({
  quote,
  first,
  locale,
  t,
}: {
  quote: QuoteRow
  first: boolean
  locale: Locale
  t: (key: CopyKey) => string
}) {
  const includes = [
    quote.includes_ferry === 1 ? quoteLegLabel('ferry', locale) : null,
    quote.includes_crane === 1 ? quoteLegLabel('crane', locale) : null,
    quote.includes_permits === 1 ? t('includes') : null,
  ].filter((part): part is string => part !== null)

  return (
    <div style={{ padding: '9px 0', borderTop: first ? 'none' : '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{quote.vendor_name_snapshot}</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{money(quote.amount_cents, locale)}</span>
      </div>
      <div style={{ ...S.chipRow, marginTop: 6 }}>
        <Chip tone={TONES.quiet} outlined>
          {quoteLegLabel(quote.leg, locale)}
        </Chip>
        <Chip tone={quote.status === 'accepted' ? TONES.strong : TONES.quiet} outlined={quote.status !== 'accepted'}>
          {quoteStatusLabel(quote.status, locale)}
        </Chip>
        {quote.quoted_on && (
          <Chip tone={TONES.quiet} outlined>
            {dateText(quote.quoted_on, locale)}
          </Chip>
        )}
      </div>
      {includes.length > 0 && (
        <p style={S.note}>
          {t('includes')}: {includes.join(', ')}
        </p>
      )}
      {quote.note && <p style={S.note}>{quote.note}</p>}
    </div>
  )
}
